import { request } from '@umijs/max';
import type { TenantInstalledApp } from '@/modules/tenant-management/services/tenant-apps.api';
import type {
  OrgNode,
  TenantMember,
  TenantMemberQuery,
} from '@/services/bff/tenant-iam.api';
import type { ApiRequestOptions } from '@/services/bff/types/common';

export type { TenantInstalledApp };

export type TenantDirectoryItem = {
  uuid: string;
  name: string;
  code?: string | null;
  logo?: string | null;
  memberCount?: number | null;
  createdAt?: string | null;
};

export type TenantDirectoryPage = {
  list: TenantDirectoryItem[];
  total: number;
  page: number;
  pageSize: number;
};

export type PlatformPermissionResult = {
  /** `资源:操作`，例如 applications:manage */
  permissions: string[];
};

export interface OrgMemberItem {
  id: string;
  userId?: string;
  name?: string;
  nickname?: string;
  username?: string;
  email?: string;
  phone?: string;
  photo?: string;
  jobNumber?: string;
  position?: string;
  isLeader?: boolean;
  isAdmin?: boolean;
  isEnabled?: boolean;
  status?: string;
  orgs?: Array<{
    orgId?: string;
    nodeId?: string;
    code?: string;
    name?: string;
    fullLevelName?: string;
    isLeader?: boolean;
  }>;
}

export interface PlatformTenantMemberDetail extends TenantMember {
  orgs?: Array<{
    orgId?: string;
    nodeId?: string;
    code?: string;
    name?: string;
    fullLevelName?: string;
    isLeader?: boolean;
  }>;
  mainDepartmentId?: string;
  mainDepartmentCode?: string;
}

export type PlatformTenant = {
  id: string;
  name: string;
  code?: string | null;
  logo?: string | null;
  description?: string | null;
  memberCount?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type UpdatePlatformTenantPayload = {
  name?: string;
  description?: string;
  logo?: string;
};

export async function getPlatformTenant(
  tenantUuid: string,
  options?: ApiRequestOptions,
) {
  return request<PlatformTenant>(
    `/bff/platform-tenants/${encodeURIComponent(tenantUuid)}`,
    { method: 'GET', ...(options || {}) },
  );
}

export async function updatePlatformTenant(
  tenantUuid: string,
  data: UpdatePlatformTenantPayload,
  options?: ApiRequestOptions,
) {
  return request<PlatformTenant>(
    `/bff/platform-tenants/${encodeURIComponent(tenantUuid)}`,
    { method: 'PATCH', data, ...(options || {}) },
  );
}

export async function listPlatformTenantApps(
  tenantUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TenantInstalledApp[]>(
    `/bff/platform-tenants/${encodeURIComponent(tenantUuid)}/apps`,
    { method: 'GET', ...(options || {}) },
  );
}

/**
 * 获取当前登录用户是否为平台管理员 / 平台运营方（珊瑚云官方）企业成员
 */
export async function getMyPlatformPermissions(options?: ApiRequestOptions) {
  return request<PlatformPermissionResult>(
    '/bff/platform-tenants/me/permission',
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

/**
 * 平台管理员获取平台所有租户分页列表
 */
export async function listPlatformTenants(
  params?: {
    keyword?: string;
    page?: number;
    pageSize?: number;
    sortField?: 'memberCount' | 'createdAt';
    sortOrder?: 'asc' | 'desc';
  },
  options?: ApiRequestOptions,
) {
  return request<TenantDirectoryPage>('/bff/platform-tenants', {
    method: 'GET',
    params,
    timeout: 30000,
    ...(options || {}),
  });
}

/**
 * 平台管理员分页查询指定租户下的成员列表
 */
export async function listPlatformTenantMembers(
  tenantUuid: string,
  params?: TenantMemberQuery,
  options?: ApiRequestOptions,
) {
  return request<{
    list: TenantMember[];
    total?: number;
    totalElements?: number;
    totalPages?: number;
  }>(`/bff/platform-tenants/${tenantUuid}/members`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/**
 * 平台管理员获取指定租户成员的详细画像与组织归属
 */
export async function getPlatformTenantMemberDetail(
  tenantUuid: string,
  memberUuid: string,
  options?: ApiRequestOptions,
) {
  return request<PlatformTenantMemberDetail>(
    `/bff/platform-tenants/${tenantUuid}/members/${memberUuid}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

/**
 * 平台管理员获取指定租户专属的组织架构树
 */
export async function getPlatformTenantOrgTree(
  tenantUuid: string,
  options?: ApiRequestOptions,
) {
  return request<OrgNode[]>(`/bff/platform-tenants/${tenantUuid}/orgs/tree`, {
    method: 'GET',
    ...(options || {}),
  });
}

/**
 * 平台管理员获取指定租户指定部门下的成员列表
 */
export async function listPlatformTenantMembersByNode(
  tenantUuid: string,
  nodeId: string,
  params?: {
    pageNo?: number;
    pageSize?: number;
    keyword?: string;
    includeChildrenNodes?: boolean;
  },
  options?: ApiRequestOptions,
) {
  return request<{
    list: OrgMemberItem[];
    total?: number;
    totalElements?: number;
    totalPages?: number;
  }>(`/bff/platform-tenants/${tenantUuid}/orgs/nodes/${nodeId}/members`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

// ==================== B2 平台管理员成员与部门写接口 ====================

export async function createPlatformTenantMember(
  tenantUuid: string,
  data: any,
  options?: ApiRequestOptions,
) {
  return request<any>(`/bff/platform-tenants/${tenantUuid}/members`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function updatePlatformTenantMember(
  tenantUuid: string,
  userId: string,
  data: any,
  options?: ApiRequestOptions,
) {
  return request<{ success: boolean }>(
    `/bff/platform-tenants/${tenantUuid}/members/${userId}`,
    {
      method: 'PUT',
      data,
      ...(options || {}),
    },
  );
}

export async function deletePlatformTenantMember(
  tenantUuid: string,
  userId: string,
  options?: ApiRequestOptions,
) {
  return request<{ success: boolean }>(
    `/bff/platform-tenants/${tenantUuid}/members/${userId}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
}

export async function setPlatformTenantAdmins(
  tenantUuid: string,
  userIds: string[],
  options?: ApiRequestOptions,
) {
  return request<{ success: boolean }>(
    `/bff/platform-tenants/${tenantUuid}/members/admins`,
    {
      method: 'POST',
      data: { userIds },
      ...(options || {}),
    },
  );
}

export async function removePlatformTenantAdmin(
  tenantUuid: string,
  userId: string,
  options?: ApiRequestOptions,
) {
  return request<{ success: boolean }>(
    `/bff/platform-tenants/${tenantUuid}/members/admins/${userId}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
}

export async function createPlatformTenantOrg(
  tenantUuid: string,
  data: { name: string; description?: string; code?: string },
  options?: ApiRequestOptions,
) {
  return request<any>(`/bff/platform-tenants/${tenantUuid}/orgs`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function deletePlatformTenantOrg(
  tenantUuid: string,
  orgId: string,
  options?: ApiRequestOptions,
) {
  return request<{ success: boolean }>(
    `/bff/platform-tenants/${tenantUuid}/orgs/${orgId}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
}

export async function createPlatformTenantOrgNode(
  tenantUuid: string,
  data: any,
  options?: ApiRequestOptions,
) {
  return request<any>(`/bff/platform-tenants/${tenantUuid}/orgs/nodes`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function updatePlatformTenantOrgNode(
  tenantUuid: string,
  nodeId: string,
  data: any,
  options?: ApiRequestOptions,
) {
  return request<any>(
    `/bff/platform-tenants/${tenantUuid}/orgs/nodes/${nodeId}`,
    {
      method: 'PUT',
      data,
      ...(options || {}),
    },
  );
}

export async function deletePlatformTenantOrgNode(
  tenantUuid: string,
  nodeId: string,
  options?: ApiRequestOptions,
) {
  return request<{ success: boolean }>(
    `/bff/platform-tenants/${tenantUuid}/orgs/nodes/${nodeId}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
}

export async function setPlatformTenantNodeLeader(
  tenantUuid: string,
  nodeId: string,
  data: any,
  options?: ApiRequestOptions,
) {
  return request<{ success: boolean }>(
    `/bff/platform-tenants/${tenantUuid}/orgs/nodes/${nodeId}/leader`,
    {
      method: 'PUT',
      data,
      ...(options || {}),
    },
  );
}

export async function addMembersToPlatformTenantNode(
  tenantUuid: string,
  nodeId: string,
  userIds: string[],
  options?: ApiRequestOptions,
) {
  return request<{ success: boolean }>(
    `/bff/platform-tenants/${tenantUuid}/orgs/nodes/${nodeId}/members`,
    {
      method: 'POST',
      data: { userIds },
      ...(options || {}),
    },
  );
}

export async function removeMembersFromPlatformTenantNode(
  tenantUuid: string,
  nodeId: string,
  userIds: string[],
  options?: ApiRequestOptions,
) {
  return request<{ success: boolean }>(
    `/bff/platform-tenants/${tenantUuid}/orgs/nodes/${nodeId}/members`,
    {
      method: 'DELETE',
      data: { userIds },
      ...(options || {}),
    },
  );
}

export async function moveMembersAcrossPlatformTenantNodes(
  tenantUuid: string,
  data: { userIds: string[]; sourceNodeId: string; targetNodeId: string },
  options?: ApiRequestOptions,
) {
  return request<{ success: boolean }>(
    `/bff/platform-tenants/${tenantUuid}/orgs/nodes/move-members`,
    {
      method: 'POST',
      data,
      ...(options || {}),
    },
  );
}
